import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { Tag, MapPin, Edit, Trash2, Repeat, CircleDollarSign, Sparkles, Navigation, HelpCircle } from 'lucide-react';
import FuturisticCard from '../futuristicCard/FuturisticCard';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '../../api/api';
import ImageWithFallback from '../common/ImageWithFallback';

const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const d = R * c; // Distance in km
    return d.toFixed(1);
};
const getCashStyle = (flexibility) => {
  switch (flexibility) {
    case 'can_add':
      // Open to add money -> Green (Deal Maker!)
      return {
        card: 'bg-emerald-950/80 dark:bg-emerald-950/90 border-emerald-500/50',
        icon: 'text-emerald-400',
        text: 'text-emerald-200'
      };
    case 'can_add_or_receive':
      // Both -> Emerald & Teal Gradient (Super Cash Flexible!)
      return {
        card: 'bg-gradient-to-r from-emerald-950/90 via-teal-950/90 to-emerald-900/90 border-emerald-400/60',
        icon: 'text-emerald-300',
        text: 'text-emerald-100'
      };
    case 'can_receive':
      // Asks for money -> Warm Amber
      return {
        card: 'bg-amber-950/80 dark:bg-amber-950/90 border-amber-500/50',
        icon: 'text-amber-400',
        text: 'text-amber-200'
      };
    case 'prefer_exchange':
    default:
      // Items only / Not into cash -> Red / Rose (Rigid)
      return {
        card: 'bg-rose-950/80 dark:bg-rose-950/90 border-rose-500/50',
        icon: 'text-rose-400',
        text: 'text-rose-200'
      };
  }
};

export default function ItemCard({ item, showActions = false, onEdit, onDelete, viewMode = 'grid' }) {
  const { t, language } = useLanguage();
  const { currency, convertCurrency } = useCurrency();
  const [searchParams] = useSearchParams();

  const userLat = searchParams.get('latitude') ? Number(searchParams.get('latitude')) : null;
  const userLng = searchParams.get('longitude') ? Number(searchParams.get('longitude')) : null;
  const itemLat = item.location_geo?.coordinates?.[1];
  const itemLng = item.location_geo?.coordinates?.[0];

  const distance = calculateDistance(userLat, userLng, itemLat, itemLng);

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });

  const getCategoryName = (catId) => {
    if (!catId) return '';
    const cat = categories.find(c => c._id === catId || c.name === catId);
    if (cat) {
        return cat[`label_${language}`] || cat.label_en || cat.name;
    }
    const translated = t(catId);
    if (translated && translated !== catId) return translated;

    const snakeKey = catId.toLowerCase().replace(/-/g, '_');
    const snakeTrans = t(snakeKey);
    if (snakeTrans && snakeTrans !== snakeKey) return snakeTrans;

    return catId.replace(/_/g, ' ').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getConditionText = (cond) => {
    if (!cond) return '';
    const translated = t(cond);
    if (translated && translated !== cond) return translated;
    
    const lower = cond.toLowerCase();
    const lowerTrans = t(lower);
    if (lowerTrans && lowerTrans !== lower) return lowerTrans;

    return cond.replace(/_/g, ' ').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const displayValue = currency === 'ILS' ? convertCurrency(item.estimated_value, 'USD', 'ILS') : item.estimated_value;
  const currencySymbol = currency === 'ILS' ? '₪' : '$';
  const cashStyle = getCashStyle(item.cash_flexibility);

  const displayTitle = item.title || 'No Title';

  const handleEdit = (e) => {
    e.preventDefault();
    onEdit(item._id);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    onDelete(item._id);
  };

  const isList = viewMode === 'list';

  return (
    <Link to={`/item/${item._id}`} className="block h-full">
      <FuturisticCard className="h-full">
        {isList ? (
          /* LIST VIEW MODE (Horizontal Flex Row) */
          <div className="flex flex-row items-center h-full gap-2.5 sm:gap-4 p-2 sm:p-3">
            
            {/* LEFT SIDE: Image */}
            <div className="relative w-28 h-28 sm:w-44 sm:h-44 md:w-56 md:h-48 rounded-2xl overflow-hidden shrink-0 border border-white/10 shadow-md group flex-shrink-0">
              <ImageWithFallback
                src={item.images?.[0]}
                alt={t(item.title)}
                className="w-full h-full object-cover sm:group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-1 right-1 bg-background/90 backdrop-blur-md text-primary font-black px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-[9px] sm:text-xs border border-primary/30 shadow-md">
                {currencySymbol}{displayValue.toLocaleString()}
              </div>
              {item.open_to_other_offers && (
                 <div className="absolute bottom-1 left-1 bg-primary/95 text-primary-content px-1.5 py-0.5 rounded-full text-[7px] sm:text-[9px] font-black flex items-center gap-1 shadow-md">
                    <Sparkles size={7} className="sm:w-2.5 sm:h-2.5" /> {t('openToOtherOffers')}
                 </div>
              )}
              <div className="absolute top-1 left-1 flex flex-col gap-0.5">
                {item.status === 'pending_approval' && (
                  <div className="bg-amber-500 text-white px-1.5 py-0.5 rounded-full text-[7px] sm:text-[8px] font-black uppercase tracking-wider shadow-md">
                    {t('pendingApprovalBadge', 'ממתין לאישור מנהל')}
                  </div>
                )}
                {!item.is_visible && item.status !== 'pending_approval' && (
                  <div className="bg-red-500/90 text-white px-1.5 py-0.5 rounded-full text-[7px] sm:text-[8px] font-black uppercase tracking-wider shadow-md">
                    {t('hidden', 'Hidden')}
                  </div>
                )}
                {!item.is_available && (
                  <div className="bg-amber-500/90 text-white px-1.5 py-0.5 rounded-full text-[7px] sm:text-[8px] font-black uppercase tracking-wider shadow-md">
                    {t('unavailable', 'Unavailable')}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT SIDE: Aside all data with icons */}
            <div className="flex-1 flex flex-col justify-between min-w-0 py-0.5 h-full space-y-1.5">
              <div>
                {/* Title */}
                <h3 className="text-xs sm:text-base font-black truncate text-foreground mb-1">{displayTitle}</h3>
                
                {/* Description Preview */}
                {item.description && (
                  <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1 mb-1">
                    {item.description}
                  </p>
                )}
              </div>

              {/* BOXY ATTRIBUTES & EXCHANGE GRID */}
              <div className="mt-auto pt-1.5 border-t border-border/30 space-y-1">
                {/* Top Row: Brand & Condition */}
                <div className="grid grid-cols-2 gap-1 text-center">
                  <div className="flex flex-col items-center justify-center p-1 rounded-lg bg-indigo-950/70 dark:bg-indigo-950/80 border border-indigo-500/40 shadow-2xs">
                    <Tag className="w-3 h-3 text-indigo-400 mb-0.5 shrink-0" />
                    <span className="text-[9px] font-bold text-indigo-200 truncate w-full">
                      {item.brand && item.brand.trim() !== '' ? item.brand : t('unbranded', 'ללא מותג')}
                    </span>
                  </div>

                  <div className="flex flex-col items-center justify-center p-1 rounded-lg bg-cyan-950/70 dark:bg-cyan-950/80 border border-cyan-500/40 shadow-2xs">
                    <Sparkles className="w-3 h-3 text-cyan-400 mb-0.5 shrink-0" />
                    <span className="text-[9px] font-bold text-cyan-200 truncate w-full">
                      {getConditionText(item.condition)}
                    </span>
                  </div>
                </div>

                {/* Bottom Row: Location & Looking For */}
                <div className="grid grid-cols-2 gap-1 text-center">
                  <div className="flex flex-col items-center justify-center p-1 rounded-lg bg-blue-950/70 dark:bg-blue-950/80 border border-blue-500/40 shadow-2xs">
                    <MapPin className="w-3 h-3 text-blue-400 mb-0.5 shrink-0" />
                    <span className="text-[9px] font-bold text-blue-200 truncate w-full">
                      {item.location ? item.location.split(',')[0] : t('notSpecified')}
                    </span>
                  </div>

                  <div className="flex flex-col items-center justify-center p-1 rounded-lg bg-purple-950/70 dark:bg-purple-950/80 border border-purple-500/40 shadow-2xs">
                    <HelpCircle className="w-3 h-3 text-purple-400 mb-0.5 shrink-0" />
                    <span className="text-[9px] font-bold text-purple-200 truncate w-full">
                      {item.looking_for?.length > 0 
                        ? item.looking_for.map(id => getCategoryName(id)).join(', ')
                        : t('Anything')}
                    </span>
                  </div>
                </div>

                {/* Dedicated Cash Row */}
                <div className={`flex items-center justify-center gap-1.5 p-1.5 rounded-lg border shadow-2xs text-center ${cashStyle.card}`}>
                  <CircleDollarSign className={`w-3 h-3 shrink-0 ${cashStyle.icon}`} />
                  <span className={`text-[9px] font-black leading-tight whitespace-normal ${cashStyle.text}`}>
                    {t(item.cash_flexibility || 'prefer_exchange')}
                  </span>
                </div>
              </div>

              {showActions && (
                <div className="flex justify-end space-x-2 pt-1 border-t border-white/5">
                  <button onClick={handleEdit} className="p-1 rounded-full hover:bg-muted transition-colors">
                    <Edit className="w-3.5 h-3.5 text-blue-400" />
                  </button>
                  <button onClick={handleDelete} className="p-1 rounded-full hover:bg-muted transition-colors">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* GRID VIEW MODE (תצוגת רשת - Full Boxy Grid Card Layout) */
          <div className="flex flex-col h-full p-3 space-y-2.5">
            {/* Image */}
            <div className="relative w-full h-44 sm:h-48 rounded-2xl overflow-hidden shrink-0 border border-white/10 shadow-md group">
              <ImageWithFallback
                src={item.images?.[0]}
                alt={t(item.title)}
                className="w-full h-full object-cover sm:group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-2 right-2 bg-background/90 backdrop-blur-md text-primary font-black px-2.5 py-1 rounded-full text-xs border border-primary/30 shadow-md">
                {currencySymbol}{displayValue.toLocaleString()}
              </div>
              {item.open_to_other_offers && (
                 <div className="absolute bottom-2 left-2 bg-primary/95 text-primary-content px-2 py-0.5 rounded-full text-[9px] font-black flex items-center gap-1 shadow-md">
                    <Sparkles size={10} /> {t('openToOtherOffers')}
                 </div>
              )}
              <div className="absolute top-2 left-2 flex flex-col gap-1">
                {item.status === 'pending_approval' && (
                  <div className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider shadow-md">
                    {t('pendingApprovalBadge', 'ממתין לאישור מנהל')}
                  </div>
                )}
                {!item.is_visible && item.status !== 'pending_approval' && (
                  <div className="bg-red-500/90 text-white px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider shadow-md">
                    {t('hidden', 'Hidden')}
                  </div>
                )}
                {!item.is_available && (
                  <div className="bg-amber-500/90 text-white px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider shadow-md">
                    {t('unavailable', 'Unavailable')}
                  </div>
                )}
              </div>
            </div>

            {/* Content Details */}
            <div className="flex-1 flex flex-col justify-between space-y-2">
              <div>
                <h3 className="text-sm sm:text-base font-black truncate text-foreground mb-1">{displayTitle}</h3>
                
                {item.description && (
                  <p className="text-xs text-muted-foreground line-clamp-1 mb-1">
                    {item.description}
                  </p>
                )}
              </div>

              {/* BOXY ATTRIBUTES & EXCHANGE GRID (GRID VIEW) */}
              <div className="mt-auto pt-2 border-t border-border/30 space-y-1">
                {/* Top Row: Brand & Condition */}
                <div className="grid grid-cols-2 gap-1 text-center">
                  <div className="flex flex-col items-center justify-center p-1.5 rounded-lg bg-indigo-950/70 dark:bg-indigo-950/80 border border-indigo-500/40 shadow-2xs">
                    <Tag className="w-3.5 h-3.5 text-indigo-400 mb-0.5 shrink-0" />
                    <span className="text-[9px] font-bold text-indigo-200 truncate w-full">
                      {item.brand && item.brand.trim() !== '' ? item.brand : t('unbranded', 'ללא מותג')}
                    </span>
                  </div>

                  <div className="flex flex-col items-center justify-center p-1.5 rounded-lg bg-cyan-950/70 dark:bg-cyan-950/80 border border-cyan-500/40 shadow-2xs">
                    <Sparkles className="w-3.5 h-3.5 text-cyan-400 mb-0.5 shrink-0" />
                    <span className="text-[9px] font-bold text-cyan-200 truncate w-full">
                      {getConditionText(item.condition)}
                    </span>
                  </div>
                </div>

                {/* Row 2: Location & Looking For */}
                <div className="grid grid-cols-2 gap-1 text-center">
                  <div className="flex flex-col items-center justify-center p-1.5 rounded-lg bg-blue-950/70 dark:bg-blue-950/80 border border-blue-500/40 shadow-2xs">
                    <MapPin className="w-3.5 h-3.5 text-blue-400 mb-0.5 shrink-0" />
                    <span className="text-[9px] font-bold text-blue-200 truncate w-full">
                      {item.location ? item.location.split(',')[0] : t('notSpecified')}
                    </span>
                  </div>

                  <div className="flex flex-col items-center justify-center p-1.5 rounded-lg bg-purple-950/70 dark:bg-purple-950/80 border border-purple-500/40 shadow-2xs">
                    <HelpCircle className="w-3.5 h-3.5 text-purple-400 mb-0.5 shrink-0" />
                    <span className="text-[9px] font-bold text-purple-200 truncate w-full">
                      {item.looking_for?.length > 0 
                        ? item.looking_for.map(id => getCategoryName(id)).join(', ')
                        : t('Anything')}
                    </span>
                  </div>
                </div>

                {/* Row 3: Dedicated Full-Width Cash Flexibility Row (Dynamic Color + Full Multiline Visibility) */}
                <div className={`flex items-center justify-center gap-1.5 p-1.5 rounded-lg border shadow-2xs text-center ${cashStyle.card}`}>
                  <CircleDollarSign className={`w-3.5 h-3.5 shrink-0 ${cashStyle.icon}`} />
                  <span className={`text-[9px] sm:text-[10px] font-black leading-tight whitespace-normal ${cashStyle.text}`}>
                    {t(item.cash_flexibility || 'prefer_exchange')}
                  </span>
                </div>
              </div>

              {showActions && (
                <div className="flex justify-end space-x-2 pt-1 border-t border-white/5">
                  <button onClick={handleEdit} className="p-1 rounded-full hover:bg-muted transition-colors">
                    <Edit className="w-3.5 h-3.5 text-blue-400" />
                  </button>
                  <button onClick={handleDelete} className="p-1 rounded-full hover:bg-muted transition-colors">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </FuturisticCard>
    </Link>
  );
}
